import {enableProdMode, ErrorHandler, importProvidersFrom, NgZone, provideZoneChangeDetection} from '@angular/core';
import {environment} from './environments/environment';
import {bootstrapApplication, Title} from '@angular/platform-browser';
import {AppComponent} from './app/app.component';
import {ActionReducerMap, provideStore, Store} from '@ngrx/store';
import {SessionReducer} from './core/reducers/session.reducer';
import {TitleReducer} from './core/reducers/title.reducer';
import {AppsReducer} from './core/reducers/apps.reducer';
import {TilesReducer} from './core/reducers/tiles.reducer';
import {HubsReducer} from './core/reducers/hubs.reducer';
import {DevicesReducer} from './core/reducers/devices.reducer';
import {PointsReducer} from './core/reducers/points.reducer';
import {AlarmsReducer} from './core/reducers/alarms.reducer';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {InMemoryScrollingFeature, InMemoryScrollingOptions, provideRouter, withDebugTracing, withInMemoryScrolling} from '@angular/router';
import {appRoutes} from './app/app.module';
import {provideAnimations} from '@angular/platform-browser/animations';
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr} from '@angular/common/http';
import {AppInterceptor} from './core/interceptors/app.interceptor';
import {NativeService} from './core/services/native.service';
import {SessionService} from './core/services/session.service';
import {AlarmsService} from './core/services/alarms.service';
import {UrlService} from './core/services/url.service';
import {provideCharts, withDefaultRegisterables} from 'ng2-charts';
import {AppState} from './app/app.state';
import {NativeIosService} from './core/services/native-ios.service';
import {NativeAndroidService} from './core/services/native-android.service';
import {NativeWebService} from './core/services/native-web.service';
import {DateFnsModule} from 'ngx-date-fns';
import { DatePipe } from '@angular/common';
import { MARKED_EXTENSIONS, provideMarkdown } from 'ngx-markdown';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';
import { firebaseConfig } from './environments/firebase/firebase.config';
import { katexBracketDelimitersExtension } from './shared/utils/katex-delimiters.util';
import { Agent } from '@newrelic/browser-agent/loaders/agent';
import { JSErrors } from '@newrelic/browser-agent/features/jserrors';
import { SessionReplay } from '@newrelic/browser-agent/features/session_replay';
import { AgentConfiguration, NewRelicCapacitorPlugin } from '@newrelic/newrelic-capacitor-plugin';
import { newrelicConfig } from './environments/newrelic/newrelic.config';
import { GlobalErrorHandler } from './core/services/global-error-handler';
import { isPlaceholderToken } from './core/services/error-reporting.service';


if (environment.production) {
  enableProdMode();
}

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

function createNativeService(
  http: HttpClient,
  session: SessionService,
  store: Store<AppState>,
  alarmsService: AlarmsService,
  urlService: UrlService,
  ngZone: NgZone,
): NativeService {
  if (environment.iOS) {
    return new NativeIosService(http, session, store, alarmsService, urlService, ngZone);
  }

  if (environment.android) {
    return new NativeAndroidService(http, session, store, alarmsService, urlService, ngZone);
  }

  return new NativeWebService(http, session, store, alarmsService, urlService, ngZone);
}


function initNewRelicAgent(): void {
  if (environment.iOS || environment.android) {
    const appToken = environment.iOS ? newrelicConfig.iosAppToken : newrelicConfig.androidAppToken;

    if (isPlaceholderToken(appToken)) {
      return;
    }

    const agentConfig: AgentConfiguration = {
      analyticsEventEnabled: true,
      crashReportingEnabled: true,
      networkRequestEnabled: false,
      loggingEnabled: false,
      offlineStorageEnabled: true,
    };

    NewRelicCapacitorPlugin.start({appKey: appToken, agentConfiguration: agentConfig});

    return;
  }

  if (isPlaceholderToken(newrelicConfig.browserLicenseKey)) {
    return;
  }

  new Agent({
    info: {
      applicationID: String(newrelicConfig.browserApplicationId),
      licenseKey: newrelicConfig.browserLicenseKey,
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      sa: 1,
    },
    init: {
      browser_consent_mode: {enabled: false},
      privacy: {cookies_enabled: true},
      session_replay: {
        enabled: newrelicConfig.sessionReplayEnabled,
        block_selector: 'canvas',
      },
    },
    loader_config: {
      accountID: newrelicConfig.browserAccountId,
      agentID: newrelicConfig.browserAgentId,
      applicationID: newrelicConfig.browserApplicationId,
      licenseKey: newrelicConfig.browserLicenseKey,
      trustKey: newrelicConfig.browserTrustKey,
    },
    features: [JSErrors, SessionReplay],
  });
}


initNewRelicAgent();


bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideRemoteConfig(() => getRemoteConfig()),
    DatePipe,
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(
      appRoutes,
      inMemoryScrollingFeature,
      ...(environment.production ? [] : []),
    ),

    provideStore({
      session: SessionReducer,
      title: TitleReducer,
      apps: AppsReducer,
      tiles: TilesReducer,
      hubs: HubsReducer,
      devices: DevicesReducer,
      points: PointsReducer,
      alarms: AlarmsReducer,
    } as ActionReducerMap<AppState>),
    provideStoreDevtools(),
    importProvidersFrom(
      DateFnsModule.forRoot(),
    ),
    Title,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: NativeService,
      useFactory: createNativeService,
      deps: [HttpClient, SessionService, Store, AlarmsService, UrlService, NgZone],
    },
    provideCharts(withDefaultRegisterables()),
    provideMarkdown({
      loader: HttpClient,
      markedExtensions: [
        { provide: MARKED_EXTENSIONS, useValue: katexBracketDelimitersExtension(), multi: true },
      ],
    })
  ],
}).catch(err => console.error(err));


